import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Repository} from '@app/@core/repositories/repository';
import {ReserveModel} from '@app/models/reserve';
import {format} from 'date-fns';
import {environment} from '@env/environment';
import Reserve = ReserveModel.Reserve;
import Payment = ReserveModel.Payment;
import ReserveStatus = ReserveModel.ReserveStatus;
import {delay, map} from 'rxjs/operators';
import {PaymentModel} from '@app/models/payment';
import {UserRevenue} from '@app/modules/report/user-reserve-report/user-reserve-report.component';
import {ReserveSearchMethod} from '@app/modules/reserve/reserve-constants';
import {ReservesLeisureResponse} from '@app/models/v3/reserve-leisure';
import {Primitive} from "@shared/interfaces/primitive";


export interface ReserveSearchParams {
    from: Date;
    to: Date;
    status: string[];
    courseId?: string;
    invoice?: string;
    user?: string;
}

export interface BookingRequest {
    userId: string;
    slotId: string;
    persons: number;
    carts: number;
    payment: Payment;
    voucherCode: string;
    channel: string;

    memberId?: string;
    memberName?: string;
    jaNumber?: string;
}

export interface GetTransactionParam {
    from: Date;
    to: Date;
    dateField: string;
    statuses: ReserveStatus[];
    userIds: string[];
    courseIds: string[];
    gdInvoiceId?: string;
    code?: string;
}

export interface GetTransactionLeisureParam {
    from: Date;
    to: Date;
    dateField: string;
    statuses: ReserveStatus[];
    userIds: string[];
    shopIds: string[];
    gdInvoiceId?: string;
    code?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ReserveRepository extends Repository<Reserve> {

    constructor(http: HttpClient) {
        super(http, 'reserve');
    }

    searchByTransactionDate(fromDate: Date, toDate: Date, statusList: string[]): Observable<Reserve[]> {
        let params = new HttpParams()
            .set('fromDate', format(fromDate, 'dd-MM-yyy'))
            .set('toDate', format(toDate, 'dd-MM-yyy'));

        params = statusList.reduce((p, status) => p.append('statusList', status), params);

        return this.http.get<Reserve[]>(`${environment.apiUrl}/api/reserve/search`, {params});
    }

    searchByPlayDate(fromDate: Date, toDate: Date, statusList: string[]): Observable<Reserve[]> {
        let params = new HttpParams()
            .set('fieldDateBy', 'slot')
            .set('fromDate', format(fromDate, 'dd-MM-yyy'))
            .set('toDate', format(toDate, 'dd-MM-yyy'));

        params = statusList.reduce((p, status) => p.append('statusList', status), params);

        return this.http.get<Reserve[]>(`${environment.apiUrl}/api/reserve/search`, {params});
    }

    searchTransaction(param: Partial<GetTransactionParam>): Observable<Reserve[]> {
        const params = {
            ...param,
            from: param.from ? format(param.from, 'yyyy-MM-dd') : '',
            to: param.from ? format(param.to, 'yyyy-MM-dd') : ''
        };

        if (!params.dateField) {
            console.error('(BE bug) dateField override to created');
            params.dateField = 'created';
        }

        // Bug dev not return array
        return this.http.get<any>(`${this.entityUrl}/transaction`, {params}).pipe(
            map(r => {
                return r.success ? r.model ?? [] : r; // Bug dev not return array; it return {success: true, model: []}
            })
        );
    }

    getRevokedTransactions(date: Date): Observable<Reserve[]> {
        const params = {date: format(date, 'yyyy-MM-dd')};
        return this.http.get<any>(`${this.entityUrl}/revokedTransactions`, {params});
    }

    queryPayment(reserveId: string): Observable<boolean> {
        return this.http.get<any>(`${this.entityUrl}/${reserveId}/query-status`).pipe(map(r => r.value));
    }


    paidReserve(reserveId: string, payment: Payment): Observable<Reserve> {
        return this.http.put<Reserve>(`${this.entityUrl}/paid`, {payment, reserveId});
    }

    scan(reserveId: string): Observable<void> {
        return this.http.put<any>(`${this.entityUrl}/scan`, [reserveId]);
    }

    cancelPaid(reserveId: string): Observable<Reserve> {
        return this.http.put<Reserve>(`${this.entityUrl}/${reserveId}/cancel-paid`, {});
    }

    cancelUnPaid(reserveId: string): Observable<Reserve> {
        return this.http.put<Reserve>(`${this.entityUrl}/${reserveId}/cancel-unpaid`, {});
    }

    refundManual(reserveId: string, note: string): Observable<void> {
        return this.http.put<void>(`${this.entityUrl}/refund-manual/${reserveId}`, {note});
    }

    newBooking(model: BookingRequest, jaFiles: File[]): Observable<Reserve> {
        const form = new FormData();
        form.append('json', JSON.stringify(model));
        if (jaFiles) {
            for (const file of jaFiles) {
                form.append('files', file);
            }
        }
        return this.http.post<any>(`${this.entityUrl}/booking`, form).pipe(this.mapSingle());
    }

    recoverBooking(reserveId: string): Observable<Reserve> {
        return this.http.put<Reserve>(`${this.entityUrl}/recover`, {reserveId});
    }

    sendEmail(reserveId: string, toUser: boolean, toCourse: boolean): Observable<Reserve> {
        const body = {toUser, toCourse};
        return this.http.put<Reserve>(`${environment.apiUrl}/api/reserve/${reserveId}/resendEmail`, body);
    }

    updateTransferStatus(reserveIds: string[]): Observable<Reserve[]> {
        return this.http.put<any>(`${this.entityUrl}/costs/transferDate/list`, reserveIds);
    }

    activeCustomer(from: Date, to: Date, paid: boolean): Observable<UserRevenue[]> {
        const params = {
            from: format(from, 'yyyy-MM-dd'),
            to: format(to, 'yyyy-MM-dd'),
            paid
        };

        return this.http.get<any[]>(`${environment.apiUrl}/api/reserve/activeCustomer/`, {params});
    }

    countNewRegisterUser(from: Date, to: Date): Observable<any> {
        const params = {
            from: format(from, 'yyyy-MM-dd'),
            to: format(to, 'yyyy-MM-dd'),
        };

        return this.http.get<any>(`${environment.apiUrl}/api/reserve/countNewRegisterUser/`, {params}).pipe(this.mapSingle());
    }

    createTagthai(formModel: {
        persons: number;
        carts: number;
        reserveId: undefined;
        caddies: number;
        slotId: undefined
    }) {
        return this.http.post<Reserve>(`${this.entityUrl}/tag-thai`, formModel);
    }

    changeTeeTime(reserveId: string, slotId: string): Observable<Reserve> {
        return this.http.put<Reserve>(`${this.entityUrl}/booking/edit`, {reserveId, slotId});
    }

    // Transfer Query
    getScheduledTransfers(date: Date): Observable<Reserve[]> {
      const params = new HttpParams().set('date', format(date, 'yyyy-MM-dd'));
      return this.http.get<Reserve[]>(`${this.entityUrl}/transfer/scheduled`, { params });
    }

    getOverdueTransfers(): Observable<Reserve[]> {
      return this.http.get<Reserve[]>(`${this.entityUrl}/transfer/overdue`);
    }

    getRefundPendingTransfers(): Observable<Reserve[]> {
        return this.http.get<Reserve[]>(`${this.entityUrl}/transfer/refund-pending`);
    }

    getRefundPendingTransfersCount(): Observable<Primitive<number>> {
        return this.http.get<any>(`${this.entityUrl}/transfer/refund-pending/count`);
    }

    // Cost Management
    applyCoupon(reserveId: string, couponId: string, numberOfCoupon: number): Observable<ReserveModel.Reserve> {
        return this.http.post<Reserve>(`${this.entityUrl}/cost/applyCoupon`, {reserveId, couponId, numberOfCoupon});
    }

    removeCoupon(reserveId: string): Observable<Reserve> {
        return this.http.delete<Reserve>(`${this.entityUrl}/cost/removeCoupon`, {params: {reserveId}});
    }

    updateCredit(reserveId: string, credit: number): Observable<Reserve> {
        return this.http.post<Reserve>(`${this.entityUrl}/cost/updateCredit`, {reserveId, credit});
    }

    updateAdditionalCost(reserveId: string, amount: number): Observable<Reserve> {
        return this.http.post<Reserve>(`${this.entityUrl}/cost/updateAdditionalCost`, {reserveId, amount});
    }

    updateCostNote(reserveId: string, note: string): Observable<Reserve> {
        return this.http.post<Reserve>(`${this.entityUrl}/cost/updateNote`, {reserveId, note});
    }

    markReservesAsTransferred(reserves: {
        reserveId: string;
        amount: number;
    }[]): Observable<void> {
      return this.http.put<void>(`${this.entityUrl}/costs/status/transferred/batch`, reserves);
    }

    // markReservesAsTransferred(reserveIds: string[]): Observable<void> {
    //     return this.http.put<void>(`${this.entityUrl}/costs/status/transferred/batch`, reserveIds);
    // }

    markReserveAsRefundPending(reserveId: string): Observable<void> {
        return this.http.put<void>(`${this.entityUrl}/costs/status/refund/pending`, {reserveId});
    }

    markReserveAsRefundCompleted(reserveId: string, coupons = 0, creditAmount = 0): Observable<void> {
        return this.http.put<void>(`${this.entityUrl}/costs/status/refund/completed`, {reserveId, coupons, creditAmount});
    }

    markReserveAsRefundRefused(reserveId: string): Observable<void> {
        return this.http.put<void>(`${this.entityUrl}/costs/status/refund/refused?reserveId=${reserveId}`, {});
    }
}
